import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  GripVertical,
  Check,
  Calendar,
  BookOpen,
  Pencil,
  Trash2,
  ChevronLeft,
  Loader2,
  Copy,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEmploisDuTemps, useUpdateEmploiDuTemps, useDeleteEmploiDuTemps, useCreateEmploiDuTemps } from '../hooks/use-emploi-du-temps'
import { usePeriodesEtudiant } from '../hooks/use-calendrier'
import { useMatieres } from '@/features/matieres/hooks/use-matieres'
import { useProgrammes } from '@/features/programmes/hooks/use-programmes'
import type { WeekDay } from '@/shared/types/common'
import type { Matiere, Programme } from '@/shared/types/admin'
import type { EmploiDuTempsDto, MatiereAvecJoursRequest, CreateEmploiDuTempsRequest } from '@/shared/types/emploi-du-temps'
import type { PeriodeEtudiantDto } from '@/shared/types/calendrier'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'

// ── Constants ───────────────────────────────────────────────

const WORK_DAYS: WeekDay[] = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI']

const DAY_LABELS: Record<WeekDay, string> = {
  LUNDI: 'Lundi',
  MARDI: 'Mardi',
  MERCREDI: 'Mercredi',
  JEUDI: 'Jeudi',
  VENDREDI: 'Vendredi',
  SAMEDI: 'Samedi',
  DIMANCHE: 'Dimanche',
}

const DAY_COLORS: Record<string, { bg: string; border: string; accent: string }> = {
  LUNDI: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700' },
  MARDI: { bg: 'bg-violet-50', border: 'border-violet-200', accent: 'text-violet-700' },
  MERCREDI: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700' },
  JEUDI: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700' },
  VENDREDI: { bg: 'bg-rose-50', border: 'border-rose-200', accent: 'text-rose-700' },
  SAMEDI: { bg: 'bg-cyan-50', border: 'border-cyan-200', accent: 'text-cyan-700' },
}

const PERIODE_TYPES_FILTER = ['ECOLE', 'SEMAINE_A', 'SEMAINE_B']

type DayAssignments = Record<WeekDay, number[]>

const emptyAssignments = (): DayAssignments =>
  ({ LUNDI: [], MARDI: [], MERCREDI: [], JEUDI: [], VENDREDI: [], SAMEDI: [], DIMANCHE: [] })

// ── Helpers ─────────────────────────────────────────────────

/** Pivot day-centric → matiere-centric for the API request */
function pivotToMatiereRequest(
  etudiantId: number,
  dayAssignments: DayAssignments,
): MatiereAvecJoursRequest[] {
  const map = new Map<number, WeekDay[]>()
  for (const day of WORK_DAYS) {
    for (const matiereId of dayAssignments[day]) {
      const existing = map.get(matiereId) ?? []
      if (!existing.includes(day)) {
        map.set(matiereId, [...existing, day])
      }
    }
  }
  return Array.from(map.entries()).map(([matiereId, jours]) => ({
    etudiantId,
    matiereId,
    jours,
  }))
}

/** Pivot matiere-centric (from API) → day-centric for the UI */
function pivotFromDto(edt: EmploiDuTempsDto): DayAssignments {
  const result = emptyAssignments()
  for (const em of edt.etudiantMatieres ?? []) {
    if (em.jour && result[em.jour] && !result[em.jour].includes(em.matiereId)) {
      result[em.jour].push(em.matiereId)
    }
  }
  return result
}

// ── Sub-components ──────────────────────────────────────────

function MatiereChip({
  matiere,
  onRemove,
}: {
  matiere: Matiere
  onRemove?: () => void
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors"
      style={{ backgroundColor: (matiere.couleur ?? '#6b7280') + '18', color: matiere.couleur ?? '#6b7280' }}
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: matiere.couleur ?? '#6b7280' }}
      />
      {matiere.nom}
      {matiere.estLangue && (
        <span className="rounded bg-white/60 px-1 text-[10px] font-semibold uppercase">Langue</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-black/10"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

// ── EDT Card (list mode) ────────────────────────────────────

function EdtCard({
  edt,
  onEdit,
  onDelete,
  onCopy,
}: {
  edt: EmploiDuTempsDto
  onEdit: () => void
  onDelete: () => void
  onCopy?: () => void
}) {
  const { t } = useTranslation()
  const ems = edt.etudiantMatieres ?? []
  const matiereCount = new Set(ems.map((em) => em.matiereId)).size
  const totalAssigned = ems.length

  // Detect semaine type for accent color
  const isSemaineA = edt.titre?.toLowerCase().includes('semaine a')
  const isSemaineB = edt.titre?.toLowerCase().includes('semaine b')
  const accentColor = isSemaineA ? '#3b82f6' : isSemaineB ? '#ef4444' : '#8b5cf6'
  const accentBg = isSemaineA ? 'bg-blue-500' : isSemaineB ? 'bg-red-500' : 'bg-violet-500'
  const accentLight = isSemaineA ? 'bg-blue-50' : isSemaineB ? 'bg-red-50' : 'bg-violet-50'
  const accentText = isSemaineA ? 'text-blue-600' : isSemaineB ? 'text-red-600' : 'text-violet-600'

  // Group matières by day with their colors
  const dayData = useMemo(() => {
    return WORK_DAYS.map((day) => {
      const mats = ems.filter((em) => em.jour === day)
      return { day, mats }
    })
  }, [ems])

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all hover:shadow-lg">
      {/* Colored accent bar */}
      <div className={cn('h-1.5', accentBg)} />

      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-bold text-neutral-900">
              {edt.titre || `${t('schedule.untitled', 'Emploi du temps')} #${edt.id}`}
            </h3>
            <div className="mt-1.5 flex items-center gap-2">
              <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold', accentLight, accentText)}>
                <BookOpen className="h-3 w-3" />
                {edt.programme?.libelle ?? '—'}
              </span>
              <span className="text-[11px] text-neutral-400">
                {matiereCount} matière{matiereCount > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="ml-2 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            {onCopy && (
              <button
                type="button"
                onClick={onCopy}
                className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-blue-50 hover:text-blue-500"
                title={isSemaineA ? 'Copier vers Semaine B' : 'Copier vers Semaine A'}
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={onEdit}
              className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-primary"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Day grid with matière dots */}
      <div className="grid grid-cols-6 gap-0 border-t border-neutral-100">
        {dayData.map(({ day, mats }) => {
          const colors = DAY_COLORS[day]
          const hasContent = mats.length > 0
          return (
            <div key={day} className={cn('flex flex-col items-center py-3', hasContent ? '' : 'opacity-40')}>
              {/* Day label */}
              <span className={cn('text-[10px] font-bold uppercase tracking-wider', hasContent ? colors.accent : 'text-neutral-300')}>
                {DAY_LABELS[day].slice(0, 3)}
              </span>
              {/* Matière dots */}
              <div className="mt-2 flex flex-col items-center gap-1">
                {mats.length > 0 ? (
                  mats.map((em, i) => (
                    <div
                      key={`${em.matiereId}-${i}`}
                      className="h-2.5 w-2.5 rounded-full ring-1 ring-white"
                      style={{ backgroundColor: em.matiereCouleur ?? '#94a3b8' }}
                      title={em.matiereNom}
                    />
                  ))
                ) : (
                  <div className="h-2.5 w-2.5 rounded-full bg-neutral-200" />
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer: matière legend */}
      {totalAssigned > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 border-t border-neutral-100 px-5 py-2.5">
          {Array.from(new Map(ems.map((em) => [em.matiereId, em])).values()).map((em) => (
            <span key={em.matiereId} className="flex items-center gap-1.5 text-[11px] text-neutral-500">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: em.matiereCouleur ?? '#94a3b8' }}
              />
              {em.matiereNom}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Editor (create/edit form with pool + day columns) ───────

function EdtEditor({
  etudiantId,
  existing,
  matieres,
  programmes,
  periodes,
  onSave,
  onCancel,
  isSaving,
}: {
  etudiantId: number
  existing?: EmploiDuTempsDto
  matieres: Matiere[]
  programmes: Programme[]
  periodes: PeriodeEtudiantDto[]
  onSave: (payload: {
    titre: string
    programmeId: number
    periodeEtudiantId: number | null
    matieres: MatiereAvecJoursRequest[]
  }) => void
  onCancel: () => void
  isSaving: boolean
}) {
  const { t } = useTranslation()

  // Filtered periodes (ECOLE, SEMAINE_A, SEMAINE_B only)
  const filteredPeriodes = useMemo(
    () => periodes.filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode)),
    [periodes],
  )

  // Active programmes only
  const activeProgrammes = useMemo(
    () => programmes.filter((p) => p.actif),
    [programmes],
  )

  // Form state — auto-select if only one option
  const [titre, setTitre] = useState(existing?.titre ?? '')
  const [programmeId, setProgrammeId] = useState<number>(
    existing?.programme?.numeroProgramme
      ?? (programmes.filter((p) => p.actif).length === 1 ? programmes.filter((p) => p.actif)[0].numeroProgramme : (programmes.filter((p) => p.actif)[0]?.numeroProgramme ?? 0)),
  )
  const [periodeEtudiantId, setPeriodeEtudiantId] = useState<number>(
    existing?.periodeEtudiantId
      ?? (periodes.filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode)).length === 1 ? periodes.filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode))[0].id : (periodes.filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode))[0]?.id ?? 0)),
  )
  const [dayMatieres, setDayMatieres] = useState<DayAssignments>(
    existing ? pivotFromDto(existing) : emptyAssignments(),
  )

  // Drag state
  const [draggedMatiere, setDraggedMatiere] = useState<number | null>(null)
  const [dragOverDay, setDragOverDay] = useState<WeekDay | null>(null)

  // Active matières only
  const activeMatieres = useMemo(
    () => matieres.filter((m) => m.actif),
    [matieres],
  )

  // Map for quick lookup
  const matiereMap = useMemo(() => {
    const map = new Map<number, Matiere>()
    for (const m of matieres) map.set(m.id, m)
    return map
  }, [matieres])

  // Count how many days each matière is assigned to
  const matiereDayCounts = useMemo(() => {
    const counts = new Map<number, number>()
    for (const day of WORK_DAYS) {
      for (const mId of dayMatieres[day]) {
        counts.set(mId, (counts.get(mId) ?? 0) + 1)
      }
    }
    return counts
  }, [dayMatieres])

  // ── Handlers ──────────────────────────────────────────

  const addToDay = useCallback((day: WeekDay, matiereId: number) => {
    setDayMatieres((prev) => {
      if (prev[day].includes(matiereId)) return prev
      return { ...prev, [day]: [...prev[day], matiereId] }
    })
  }, [])

  const removeFromDay = useCallback((day: WeekDay, matiereId: number) => {
    setDayMatieres((prev) => ({
      ...prev,
      [day]: prev[day].filter((id) => id !== matiereId),
    }))
  }, [])

  const handleDragStart = useCallback((matiereId: number) => {
    setDraggedMatiere(matiereId)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, day: WeekDay) => {
    e.preventDefault()
    setDragOverDay(day)
  }, [])

  const handleDrop = useCallback(
    (day: WeekDay) => {
      if (draggedMatiere !== null) {
        addToDay(day, draggedMatiere)
      }
      setDraggedMatiere(null)
      setDragOverDay(null)
    },
    [draggedMatiere, addToDay],
  )

  const handleDragEnd = useCallback(() => {
    setDraggedMatiere(null)
    setDragOverDay(null)
  }, [])

  const handleSubmit = () => {
    if (!programmeId || !periodeEtudiantId) return
    onSave({
      titre,
      programmeId,
      periodeEtudiantId,
      matieres: pivotToMatiereRequest(etudiantId, dayMatieres),
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'
  const selectClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  const totalAssigned = WORK_DAYS.reduce((sum, d) => sum + dayMatieres[d].length, 0)

  return (
    <div className="space-y-4">
      {/* Config card */}
      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-100 px-6 py-4">
          <h2 className="text-base font-semibold text-primary">
            {t('schedule.config', 'Configuration')}
          </h2>
        </div>
        <div className="grid grid-cols-3 gap-4 p-6">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              {t('schedule.title', 'Titre')}
            </label>
            <input
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder={t('schedule.titlePlaceholder', 'Emploi du temps...')}
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              {t('schedule.programme', 'Programme')}
            </label>
            <select
              value={programmeId}
              onChange={(e) => setProgrammeId(Number(e.target.value))}
              className={selectClass}
            >
              {activeProgrammes.map((p) => (
                <option key={p.numeroProgramme} value={p.numeroProgramme}>
                  {p.libelle}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              {t('schedule.period', 'Période')}
            </label>
            <select
              value={periodeEtudiantId}
              onChange={(e) => setPeriodeEtudiantId(Number(e.target.value))}
              className={selectClass}
            >
              {filteredPeriodes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.titre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Matière pool (full width, fixed height, scrollable) */}
      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">
              {t('schedule.availableSubjects', 'Matières disponibles')}
            </h3>
            <p className="mt-0.5 text-xs text-neutral-400">
              {t('schedule.dragHint', 'Glissez vers un jour')}
            </p>
          </div>
          <span className="text-xs text-neutral-400">
            {activeMatieres.length} {t('schedule.subjects', 'matière')}{activeMatieres.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="max-h-[160px] overflow-y-auto p-3">
          <div className="flex flex-wrap gap-2">
            {activeMatieres.map((m) => {
              const dayCount = matiereDayCounts.get(m.id) ?? 0
              return (
                <div
                  key={m.id}
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'flex cursor-grab items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-sm transition-all hover:shadow-md active:cursor-grabbing',
                    draggedMatiere === m.id && 'opacity-30',
                  )}
                >
                  <GripVertical className="h-3.5 w-3.5 shrink-0 text-neutral-300" />
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: m.couleur ?? '#6b7280' }}
                  />
                  <span className="whitespace-nowrap text-sm font-medium text-neutral-700">
                    {m.nom}
                  </span>
                  {dayCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
                      {dayCount}
                    </span>
                  )}
                </div>
              )
            })}
            {activeMatieres.length === 0 && (
              <p className="w-full py-4 text-center text-xs text-neutral-400">
                {t('schedule.noSubjects', 'Aucune matière active')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Day columns */}
      <div className="grid grid-cols-6 gap-3">
        {WORK_DAYS.map((day) => {
            const colors = DAY_COLORS[day]
            const selectedIds = dayMatieres[day]
            const selected = selectedIds
              .map((id) => matiereMap.get(id))
              .filter(Boolean) as Matiere[]
            const isDragOver = dragOverDay === day

            return (
              <div
                key={day}
                onDragOver={(e) => handleDragOver(e, day)}
                onDragLeave={() => setDragOverDay(null)}
                onDrop={() => handleDrop(day)}
                className={cn(
                  'flex flex-col rounded-xl border-2 bg-white shadow-sm transition-all',
                  isDragOver ? 'border-primary bg-primary/5 shadow-md' : colors.border,
                )}
              >
                {/* Day header */}
                <div className={cn('rounded-t-lg px-3 py-2.5 text-center', colors.bg)}>
                  <span className={cn('text-xs font-bold uppercase tracking-wider', colors.accent)}>
                    {DAY_LABELS[day]}
                  </span>
                  {selected.length > 0 && (
                    <span className="ml-1.5 text-[10px] text-neutral-400">
                      ({selected.length})
                    </span>
                  )}
                </div>

                {/* Matières */}
                <div className="flex min-h-[140px] flex-1 flex-col gap-1.5 p-2.5">
                  {selected.map((m) => (
                    <div
                      key={m.id}
                      draggable
                      onDragStart={() => handleDragStart(m.id)}
                      onDragEnd={handleDragEnd}
                      className="flex cursor-grab items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-xs shadow-sm active:cursor-grabbing"
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: m.couleur ?? '#6b7280' }}
                      />
                      <span className="flex-1 truncate font-medium text-neutral-700">
                        {m.nom}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFromDay(day, m.id)}
                        className="shrink-0 rounded p-0.5 text-neutral-300 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}

                  {isDragOver && (
                    <div className="flex items-center justify-center rounded-md border-2 border-dashed border-primary/40 py-3 text-xs text-primary/60">
                      {t('schedule.dropHere', 'Déposer ici')}
                    </div>
                  )}

                  {selected.length === 0 && !isDragOver && (
                    <div className="flex flex-1 items-center justify-center text-xs text-neutral-300">
                      {t('schedule.noSubjectDay', 'Aucune matière')}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

      {/* Actions */}
      <div className="flex items-center justify-between border-t border-neutral-200 pt-4">
        <span className="text-sm text-neutral-500">
          {totalAssigned} {t('schedule.assignmentsTotal', 'affectation')}{totalAssigned > 1 ? 's' : ''}{' '}
          {t('schedule.across', 'sur')}{' '}
          {WORK_DAYS.filter((d) => dayMatieres[d].length > 0).length} {t('schedule.days', 'jour')}{WORK_DAYS.filter((d) => dayMatieres[d].length > 0).length > 1 ? 's' : ''}
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('common.save', 'Sauvegarder')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────

interface EmploiDuTempsTabProps {
  etudiantId: number
}

export function EmploiDuTempsTab({ etudiantId }: EmploiDuTempsTabProps) {
  const { t } = useTranslation()

  // Data
  const { data: edtList, isLoading: edtLoading } = useEmploisDuTemps(etudiantId)
  const { data: matieres, isLoading: matieresLoading } = useMatieres()
  const { data: programmes, isLoading: programmesLoading } = useProgrammes()
  const { data: periodes, isLoading: periodesLoading } = usePeriodesEtudiant(etudiantId)

  // Mutations
  const createEdt = useCreateEmploiDuTemps()
  const updateEdt = useUpdateEmploiDuTemps()
  const deleteEdt = useDeleteEmploiDuTemps()

  // UI state
  const [mode, setMode] = useState<'list' | 'edit'>('list')
  const [editingEdtId, setEditingEdtId] = useState<number | null>(null)
  const [copyDialog, setCopyDialog] = useState<{ targetLabel: string; existingId: number | null; payload: CreateEmploiDuTempsRequest } | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<number | null>(null)

  const editingEdt = useMemo(
    () => (edtList ?? []).find((e) => e.id === editingEdtId),
    [edtList, editingEdtId],
  )

  const isLoading = edtLoading || matieresLoading || programmesLoading || periodesLoading

  const handleUpdate = (payload: {
    titre: string
    programmeId: number
    periodeEtudiantId: number | null
    matieres: MatiereAvecJoursRequest[]
  }) => {
    if (!editingEdtId) return
    updateEdt.mutate(
      {
        id: editingEdtId,
        data: {
          etudiantId,
          programmeId: payload.programmeId,
          titre: payload.titre || undefined,
          periodeEtudiantId: payload.periodeEtudiantId,
          matieres: payload.matieres,
        },
      },
      { onSuccess: () => setMode('list') },
    )
  }

  const handleDelete = (id: number) => {
    setDeleteDialog(id)
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteEdt.mutate(deleteDialog)
      setDeleteDialog(null)
    }
  }

  const handleEdit = (id: number) => {
    setEditingEdtId(id)
    setMode('edit')
  }

  const handleCopySemaine = (sourceEdt: EmploiDuTempsDto, direction: 'A_TO_B' | 'B_TO_A') => {
    const list = edtList ?? []
    const filteredPeriodes = (periodes ?? []).filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode))

    const targetType = direction === 'A_TO_B' ? 'SEMAINE_B' : 'SEMAINE_A'
    const targetLabel = direction === 'A_TO_B' ? 'Semaine B' : 'Semaine A'
    const sourceLabel = direction === 'A_TO_B' ? 'Semaine A' : 'Semaine B'

    // Find the target period
    const targetPeriode = filteredPeriodes.find((p) => p.typePeriode === targetType)
    if (!targetPeriode) return

    // Check if a target EDT already exists
    const existingTarget = list.find((e) => e.periodeEtudiantId === targetPeriode.id)

    // Build matières from source EDT
    const matieresMap = new Map<number, Set<string>>()
    for (const em of sourceEdt.etudiantMatieres ?? []) {
      const existing = matieresMap.get(em.matiereId) ?? new Set()
      existing.add(em.jour)
      matieresMap.set(em.matiereId, existing)
    }
    const matieres: MatiereAvecJoursRequest[] = Array.from(matieresMap.entries()).map(
      ([matiereId, jours]) => ({
        etudiantId,
        matiereId,
        jours: Array.from(jours) as WeekDay[],
      }),
    )

    const payload: CreateEmploiDuTempsRequest = {
      programmeId: sourceEdt.programme.numeroProgramme,
      etudiantId,
      titre: sourceEdt.titre?.replace(new RegExp(sourceLabel, 'i'), targetLabel) || targetLabel,
      periodeEtudiantId: targetPeriode.id,
      matieres,
    }

    setCopyDialog({
      targetLabel,
      existingId: existingTarget?.id ?? null,
      payload,
    })
  }

  const handleCopyConfirm = () => {
    if (!copyDialog) return
    if (copyDialog.existingId) {
      updateEdt.mutate({ id: copyDialog.existingId, data: copyDialog.payload })
    } else {
      createEdt.mutate(copyDialog.payload)
    }
    setCopyDialog(null)
  }

  // Determine the copy direction for an EDT (returns null if not copyable)
  const getCopyDirection = (edt: EmploiDuTempsDto): 'A_TO_B' | 'B_TO_A' | null => {
    const filteredPeriodes = (periodes ?? []).filter((p) => PERIODE_TYPES_FILTER.includes(p.typePeriode))
    const semaineAPeriode = filteredPeriodes.find((p) => p.typePeriode === 'SEMAINE_A')
    const semaineBPeriode = filteredPeriodes.find((p) => p.typePeriode === 'SEMAINE_B')
    if (!semaineAPeriode || !semaineBPeriode) return null
    if (edt.periodeEtudiantId === semaineAPeriode.id) return 'A_TO_B'
    if (edt.periodeEtudiantId === semaineBPeriode.id) return 'B_TO_A'
    return null
  }

  // ── Loading ───────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  // ── Edit mode ─────────────────────────────────────────
  if (mode === 'edit' && editingEdt) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setMode('list')}
          className="mb-4 flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-700"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('schedule.backToList', 'Retour à la liste')}
        </button>
        <EdtEditor
          etudiantId={etudiantId}
          existing={editingEdt}
          matieres={matieres ?? []}
          programmes={programmes ?? []}
          periodes={periodes ?? []}
          onSave={handleUpdate}
          onCancel={() => setMode('list')}
          isSaving={updateEdt.isPending}
        />
      </div>
    )
  }

  // ── List mode ─────────────────────────────────────────
  const list = edtList ?? []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900">
          {t('schedule.pageTitle', 'Emplois du temps')}
        </h2>
        <p className="mt-0.5 text-sm text-neutral-500">
          {list.length} {t('schedule.edtCount', 'emploi du temps')}{list.length > 1 ? '' : ''}
        </p>
      </div>

      {/* List */}
      {list.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-200 py-16">
          <Calendar className="mb-3 h-12 w-12 text-neutral-300" />
          <p className="text-sm font-medium text-neutral-500">
            {t('schedule.empty', 'Aucun emploi du temps')}
          </p>
          <p className="mt-1 text-xs text-neutral-400">
            {t('schedule.emptyHint', 'Les emplois du temps sont créés automatiquement avec le calendrier')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {list.map((edt) => {
            const direction = getCopyDirection(edt)
            return (
              <EdtCard
                key={edt.id}
                edt={edt}
                onEdit={() => handleEdit(edt.id)}
                onDelete={() => handleDelete(edt.id)}
                onCopy={direction ? () => handleCopySemaine(edt, direction) : undefined}
              />
            )
          })}
        </div>
      )}

      {/* Copy confirmation dialog */}
      <ConfirmDialog
        open={!!copyDialog}
        variant="copy"
        title={
          copyDialog?.existingId
            ? `Remplacer la ${copyDialog.targetLabel} ?`
            : `Copier vers ${copyDialog?.targetLabel} ?`
        }
        description={
          copyDialog?.existingId
            ? `Un emploi du temps ${copyDialog.targetLabel} existe déjà. Il sera remplacé par une copie. Vous pourrez ensuite l'éditer pour ajuster les matières.`
            : `L'emploi du temps sera copié vers la ${copyDialog?.targetLabel}. Vous pourrez ensuite l'éditer pour ajuster les matières.`
        }
        confirmLabel="Copier"
        cancelLabel="Annuler"
        onConfirm={handleCopyConfirm}
        onCancel={() => setCopyDialog(null)}
      />

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={t('schedule.deleteConfirm', 'Supprimer cet emploi du temps ?')}
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </div>
  )
}
